import type React from "react";
import { useForm } from "react-hook-form";

import type { PollDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Form } from "@/components/Form/Form";
import { CheckDone02Icon } from "@/components/Icons/CheckDone02Icon";
import { twResolve } from "@/helpers/tw-resolve";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";

import { CommunityPollVoteAnswersInput } from "./CommunityPollVoteAnswersInput";

interface CommunityPollVoteFormProps {
  poll: PollDto;
}

export type CommunityPollVoteFormValues = {
  votes: string[];
};

export function CommunityPollVoteForm({ poll }: CommunityPollVoteFormProps): React.ReactNode {
  const form = useForm<CommunityPollVoteFormValues>({
    defaultValues: {
      votes: [],
    },
    mode: "onSubmit",
  });
  const { t } = useTranslation();

  const addPollVotesMutation = pollMutations.useAddPollVotes();

  const handleSubmit = async (data: CommunityPollVoteFormValues) => {
    // Handle form submission logic here
    await addPollVotesMutation.mutateAsync({
      pollId: poll.id,
      data,
    });
  };

  const isFormInvalid = Boolean(form.formState.errors.votes);

  return (
    <div className="flex w-full flex-col gap-4">
      {poll.allowMultipleAnswers && (
        <div className={twResolve("flex w-full gap-1 text-grey-700", isFormInvalid && "text-red-700")}>
          <CheckDone02Icon size={24} />
          <span className="text-caption">{t("component.community-poll.form.label")}</span>
        </div>
      )}

      <Form formMethods={form} onSubmit={handleSubmit} className="flex w-full flex-col gap-2">
        <CommunityPollVoteAnswersInput offeredAnswers={poll.answers} allowMultipleAnswers={poll.allowMultipleAnswers} />

        <Button
          className="w-full"
          isLoading={addPollVotesMutation.isPending}
          type="submit"
          data-testid="community-poll-vote-btn"
        >
          {t("component.community-poll.button.vote")}
        </Button>
      </Form>
    </div>
  );
}
